import clsx from 'clsx'
import { FC, useEffect, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useParams } from 'react-router-dom'
import { AdvertisementResponse } from '../../api'
import ProgressBar from '../../components/ProgressBar/ProgressBar'
import Button from '../../components/UI/Button/Button'
import Loader from '../../components/UI/Loader/Loader'
import Modal from '../../components/UI/Modal/Modal'
import TickleApi from '../../services/Services'
import useBrandsById from '../../services/queries/useBrandById'
import useGetBonhamImage from '../../services/queries/useGetBonhamImage'
import usePostBulkUploadImages from '../../services/queries/usePostBulkUploadImages'
import { BATCH_SIZE, processBatches } from './utils/batchProcessor'
import { CsvRow, generateBulkAdsCsv, parseCsvFile, parseSourceFile } from './utils/csvUtils'
import { processGcpImages } from './utils/imageProcessor'
import { groupImageUrlsByType, processImageUploads } from './utils/imageUploadProcessor'
import { uploadLogoImages } from './utils/logoUploader'

const BulkAdsCreation: FC = () => {
  const { brandId } = useParams()
  const [newFile, setNewFile] = useState<File | null>(null)
  const [isProcessingNewFile, setIsProcessingNewFile] = useState(false)
  const [isProcessingGcpImages, setIsProcessingGcpImages] = useState(false)
  const [gcpImagesProgress, setGcpImagesProgress] = useState<{
    current: number
    total: number
    status: string
  } | null>(null)
  const [gcpImagesMap, setGcpImagesMap] = useState<Map<number, { appleImageUrl: string; googleImageUrl: string }>>(
    new Map()
  )
  const [generatedCsvFile, setGeneratedCsvFile] = useState<File | null>(null)
  const [generatedCsvData, setGeneratedCsvData] = useState<CsvRow[]>([])
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvData, setCsvData] = useState<CsvRow[]>([])
  const [uploadProgress, setUploadProgress] = useState(0)
  const [submittedCount, setSubmittedCount] = useState(0)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isUploadingImages, setIsUploadingImages] = useState(false)
  const [imageUploadProgress, setImageUploadProgress] = useState(0)
  const [currentImageTypeStatus, setCurrentImageTypeStatus] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [successMessage, setSuccessMessage] = useState<string>('')
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const { data: brandData } = useBrandsById(brandId)
  const bulkUploadImagesMutation = usePostBulkUploadImages()
  const getBonhamImageMutation = useGetBonhamImage()
  const isMountedRef = useRef(true)

  // Cleanup loading states on unmount
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      setIsProcessing(false)
      setIsUploadingImages(false)
      setUploadProgress(0)
      setImageUploadProgress(0)
      setCurrentImageTypeStatus('')
    }
  }, [])

  const {
    getRootProps: getNewFileRootProps,
    getInputProps: getNewFileInputProps,
    open: openNewFile,
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxFiles: 1,
    onDropAccepted: async files => {
      const file = files[0]
      setNewFile(file)
      setError('')
      setSuccessMessage('')
      setIsProcessingNewFile(true)

      try {
        // Parse the file to extract Product Title, Product Collection, and URL
        const { data: sourceData, error: parseError } = await parseSourceFile(file)

        if (parseError) {
          setError(parseError)
          setIsProcessingNewFile(false)
          return
        }

        if (sourceData.length === 0) {
          setError(
            'No valid data found. Please ensure your file has "Product Title", "Product Collection", and "URL" columns.'
          )
          setIsProcessingNewFile(false)
          return
        }

        // Upload logo images from public folder
        if (!brandId) {
          setError('Brand ID is required to upload logos')
          setIsProcessingNewFile(false)
          return
        }

        const { appleWalletLogoUrl, googleWalletLogoUrl } = await uploadLogoImages(brandId, bulkUploadImagesMutation)

        // Auto-process images from static GCP bucket
        let finalImageMap = gcpImagesMap
        try {
          setIsProcessingGcpImages(true)
          // Convert sourceData to format expected by processGcpImages
          const csvDataForMatching = sourceData.map(item => ({
            'Product Image': item.productImage || '',
          }))

          // Process GCP bucket images with progress tracking
          const imageMap = await processGcpImages(
            csvDataForMatching,
            brandId,
            bulkUploadImagesMutation,
            getBonhamImageMutation,
            (current, total, status) => {
              setGcpImagesProgress({ current, total, status })
            }
          )

          finalImageMap = imageMap
          setGcpImagesMap(imageMap)

          if (imageMap.size > 0) {
            setSuccessMessage(
              `Successfully processed ${sourceData.length} unique URLs and ${imageMap.size} images from GCP bucket.`
            )
          } else {
            setSuccessMessage(
              `Successfully processed ${sourceData.length} unique URLs. No images matched from GCP bucket.`
            )
          }
        } catch (driveError) {
          // If GCP bucket processing fails, continue without images but show warning
          console.warn('GCP bucket image processing failed:', driveError)
          setSuccessMessage(
            `Successfully processed ${sourceData.length} unique URLs. GCP bucket images could not be processed: ${
              (driveError as Error).message
            }`
          )
        } finally {
          setIsProcessingGcpImages(false)
          setGcpImagesProgress(null)
        }

        // Generate CSV with the expected format, including uploaded logo URLs and GCP images if available
        const generatedCsv = generateBulkAdsCsv(
          sourceData,
          file.name,
          appleWalletLogoUrl,
          googleWalletLogoUrl,
          finalImageMap.size > 0 ? finalImageMap : undefined
        )

        // Parse the generated CSV
        const { data, error: csvParseError } = await parseCsvFile(generatedCsv)

        if (csvParseError) {
          setError(`Failed to process generated CSV: ${csvParseError}`)
          setIsProcessingNewFile(false)
          return
        }

        // Store the generated CSV for manual addition
        setGeneratedCsvFile(generatedCsv)
        setGeneratedCsvData(data)
      } catch (err) {
        setError(`Error processing file: ${(err as Error).message}`)
      } finally {
        setIsProcessingNewFile(false)
      }
    },
    onDropRejected: () => {
      setError('Invalid file. Please upload a CSV or XLSX file.')
    },
  })

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    accept: {
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
    onDropAccepted: async files => {
      const file = files[0]
      setCsvFile(file)
      setError('')

      const { data, error: parseError } = await parseCsvFile(file)
      if (parseError) {
        setError(parseError)
        return
      }
      setCsvData(data)
    },
    onDropRejected: () => {
      setError('Invalid file. Please upload a CSV file.')
    },
  })

  // Helper function to fetch ad details
  const fetchAdDetails = async (adId: string): Promise<AdvertisementResponse> => {
    const response = await TickleApi.getAdById(brandId || '', adId)
    return response.data
  }

  const handleStartProcessing = () => {
    if (csvData.length === 0) {
      setError('Please upload a CSV file')
      return
    }
    setShowConfirmModal(true)
  }

  const processBulkAds = async () => {
    if (csvData.length === 0) {
      setError('Please upload a CSV file')
      return
    }

    // Validate brand has selfServedClientId when required
    if (!brandData?.selfServedClientId) {
      setError('Brand does not have a self-served client ID. Please ensure the brand is configured correctly.')
      return
    }

    setIsProcessing(true)
    setError('')
    setSuccessMessage('')
    setUploadProgress(0)
    setSubmittedCount(0)
    setImageUploadProgress(0)

    // Promise-based cache for ad details to avoid duplicate fetches
    const adDetailsCache = new Map<string, AdvertisementResponse>()
    const adDetailsPromises = new Map<string, Promise<AdvertisementResponse>>()

    // Helper to get ad details with promise-based caching
    const getAdDetails = async (adId: string): Promise<AdvertisementResponse> => {
      const cached = adDetailsCache.get(adId)
      if (cached) {
        return cached
      }

      const existingPromise = adDetailsPromises.get(adId)
      if (existingPromise) {
        return existingPromise
      }

      const fetchPromise = fetchAdDetails(adId)
        .then(result => {
          adDetailsCache.set(adId, result)
          adDetailsPromises.delete(adId)
          return result
        })
        .catch(error => {
          adDetailsPromises.delete(adId)
          throw error
        })

      adDetailsPromises.set(adId, fetchPromise)
      return fetchPromise
    }

    // Pre-fetch all unique ad IDs
    const uniqueAdIds = new Set<string>()
    csvData.forEach(row => {
      const adId = row.AdvertId || row.advertId
      if (adId) {
        uniqueAdIds.add(adId)
      }
    })

    // Pre-fetch all unique ads in parallel
    try {
      await Promise.all(Array.from(uniqueAdIds).map(adId => getAdDetails(adId)))
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Some ad details failed to pre-fetch, will retry during processing:', err)
      }
    }

    // Collect all image URLs that need to be uploaded, grouped by type
    const imageUrlsByType = groupImageUrlsByType(csvData)

    // Upload all images using bulk upload endpoint
    if (!isMountedRef.current) return
    setIsUploadingImages(true)
    setImageUploadProgress(0)

    let imageUploadCache: Map<string, string>
    try {
      imageUploadCache = await processImageUploads({
        imageUrlsByType,
        brandId: brandId || '',
        bulkUploadImagesMutation,
        onProgress: progress => {
          if (isMountedRef.current) setImageUploadProgress(progress)
        },
        onStatusChange: status => {
          if (isMountedRef.current) setCurrentImageTypeStatus(status)
        },
      })
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error uploading images:', err)
      }
      imageUploadCache = new Map()
    } finally {
      if (isMountedRef.current) {
        setIsUploadingImages(false)
        setImageUploadProgress(0)
        setCurrentImageTypeStatus('')
      }
    }

    if (!isMountedRef.current) return
    try {
      const { uploadedCount, failedCount } = await processBatches({
        csvData,
        brandId: brandId || '',
        imageUploadCache,
        getAdDetails,
        onProgress: progress => {
          if (isMountedRef.current) setUploadProgress(progress)
        },
        onSubmittedCount: count => {
          if (isMountedRef.current) setSubmittedCount(count)
        },
        csvFileName: csvFile?.name,
      })

      setSuccessMessage(
        `Successfully created ${uploadedCount} ads. ${failedCount} failed. CSV with status has been downloaded.`
      )
      setCsvFile(null)
      setCsvData([])
    } catch (err) {
      if (isMountedRef.current) {
        setError(`Failed to create ads: ${(err as Error).message}`)
      }
    } finally {
      if (isMountedRef.current) {
        setIsProcessing(false)
      }
    }
  }

  return (
    <div className="wrapper mb-40">
      <h1 className="text-xl font-semibold mb-6">{brandData?.displayName || 'Bulk Ad Creation'}</h1>

      <div className="w-[800px] space-y-6">
        {/* New File Uploader (CSV/XLSX) */}
        <div className="rounded-xl bg-white p-6">
          <h2 className="text-lg font-semibold mb-4">Upload File (CSV or XLSX)</h2>
          <p className="text-sm text-gray-600 mb-4">
            Upload a file with &quot;Product Title&quot;, &quot;Product Collection&quot;, &quot;Product Image&quot;
            (optional), and &quot;URL&quot; columns. Duplicate URLs will be removed automatically.
          </p>

          <div
            {...getNewFileRootProps()}
            className={clsx(
              'flex flex-col items-center justify-center border-2 border-dashed rounded-md w-full h-[200px] pb-4 cursor-pointer',
              error ? 'bg-red-50 border-red-300' : 'border-gray-300 hover:border-blue-400',
              isProcessingNewFile ? 'opacity-50 cursor-not-allowed' : ''
            )}
          >
            <input {...getNewFileInputProps()} disabled={isProcessingNewFile} />
            {isProcessingNewFile || isProcessingGcpImages ? (
              <div className="text-center">
                <Loader />
                <p className="text-sm text-gray-600 mt-4">
                  {isProcessingGcpImages
                    ? gcpImagesProgress
                      ? `${gcpImagesProgress.status} (${gcpImagesProgress.current}/${gcpImagesProgress.total})`
                      : 'Processing images from GCP bucket...'
                    : 'Processing file...'}
                </p>
                {isProcessingGcpImages && gcpImagesProgress && (
                  <div className="mt-4 w-full max-w-md mx-auto">
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-primary h-2.5 rounded-full transition-all duration-300"
                        style={{
                          width: `${Math.min(100, (gcpImagesProgress.current / gcpImagesProgress.total) * 100)}%`,
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {Math.round((gcpImagesProgress.current / gcpImagesProgress.total) * 100)}% complete
                    </p>
                  </div>
                )}
              </div>
            ) : newFile ? (
              <div className="text-center w-full">
                <p className="font-semibold text-primary mb-2 break-words">{newFile.name}</p>
                <p className="text-sm text-gray-600">File size: {(newFile.size / 1024).toFixed(2)} KB</p>
                {generatedCsvData.length > 0 && (
                  <p className="text-sm text-green-600 mt-2">{generatedCsvData.length} unique URLs processed</p>
                )}
                <div className="flex flex-col gap-2 mt-4 items-center">
                  {generatedCsvFile && generatedCsvData.length > 0 && (
                    <Button
                      type="secondary"
                      onClick={() => {
                        setCsvFile(generatedCsvFile)
                        setCsvData(generatedCsvData)
                        setSuccessMessage(
                          `CSV added to Bulk Ads CSV uploader. Ready to create ${generatedCsvData.length} ads.`
                        )
                      }}
                    >
                      Add to Bulk Ads CSV
                    </Button>
                  )}
                  <Button
                    type="text"
                    onClick={() => {
                      setNewFile(null)
                      setGeneratedCsvFile(null)
                      setGeneratedCsvData([])
                      setGcpImagesMap(new Map())
                      setError('')
                      setSuccessMessage('')
                    }}
                  >
                    Remove File
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <Button type="outline" onClick={openNewFile}>
                  Select CSV or XLSX File
                </Button>
                <p className="text-sm text-gray-500 mt-4">Click to select or drag and drop</p>
                <p className="text-xs text-gray-400 mt-2">Accepted formats: .csv, .xlsx</p>
              </>
            )}
          </div>
          {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
          {successMessage && <p className="text-green-600 text-sm mt-2">{successMessage}</p>}
        </div>

        {/* CSV Upload */}
        <div className="rounded-xl bg-white p-6">
          <h2 className="text-lg font-semibold mb-4">Upload Bulk Ads CSV</h2>
          <p className="text-sm text-gray-600 mb-4">CSV should contain:</p>
          <ul className="text-sm text-gray-600 mb-4 list-disc list-inside space-y-1">
            <li>
              <strong>Required:</strong> Title, StartDate, EndDate, AppleWalletImageUrl, GoogleWalletImageUrl,
              AppleWalletLogoUrl, GoogleWalletLogoUrl, ClickthroughUrl
            </li>
            <li>
              <strong>Optional:</strong> PassColor (defaults to #000000), FontColor (defaults to #FFFFFF), AppStoreId,
              PlayStoreUrl
            </li>
            <li>
              <strong>Optional:</strong> PassOfferRedemption.Type, PassOfferRedemption.Value
            </li>
            <li>
              <strong>Optional:</strong> Back fields like &quot;AppleWalletBackFields1.Label&quot;,
              &quot;AppleWalletBackFields1.Value&quot; (also supports &quot;AppleWalletBackField1.Value&quot; typo)
            </li>
            <li>
              <strong>Optional - Notifications:</strong> Notification1.Type, Notification1.Heading,
              Notification1.SubHeading, Notification1.TimeAfterSavingUnit, Notification1.Time, etc.
              <br />
              <span className="text-xs text-gray-500 ml-4">
                Types: TIME_AFTER_SAVING (requires TimeAfterSavingUnit: INSTANT/HOUR/DAY/WEEK) or SPECIFIC_DATE_AND_TIME
                (requires Time in ISO format)
              </span>
            </li>
            <li>
              <strong>Optional:</strong> &quot;GoogleWalletBackFields1.Label&quot; (or &quot;Field&quot;),
              &quot;GoogleWalletBackFields1.Value&quot;
            </li>
            <li>
              <strong>Optional:</strong> &quot;GoogleWalletBackLinks1.Description&quot;,
              &quot;GoogleWalletBackLinks1.URIValue&quot; (or &quot;Value&quot;)
            </li>
            <li>
              Number in column name (1, 2, etc.) determines sortOrder. Field count mismatches are OK - missing optional
              columns are fine.
            </li>
          </ul>
          <div
            {...getRootProps()}
            className={clsx(
              'flex flex-col items-center justify-center border-2 border-dashed rounded-md w-full h-[200px] pb-4 cursor-pointer',
              error ? 'bg-red-50 border-red-300' : 'border-gray-300 hover:border-blue-400'
            )}
          >
            <input {...getInputProps()} />
            {csvFile ? (
              <div className="text-center">
                <p className="font-semibold text-primary mb-2">{csvFile.name}</p>
                <p className="text-sm text-gray-600">{csvData.length} rows found</p>
                <Button
                  type="text"
                  onClick={() => {
                    setCsvFile(null)
                    setCsvData([])
                    setError('')
                  }}
                  className="mt-4"
                >
                  Remove File
                </Button>
              </div>
            ) : (
              <>
                <Button type="outline" onClick={open}>
                  Select CSV File
                </Button>
                <p className="text-sm text-gray-500 mt-4">Click to select or drag and drop</p>
              </>
            )}
          </div>
          {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
          {successMessage && <p className="text-green-600 text-sm mt-2">{successMessage}</p>}
        </div>

        {/* Image Upload Progress */}
        {isUploadingImages && (
          <div className="rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0">
                <Loader />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-800">
                  {currentImageTypeStatus || 'Uploading Images...'}
                </h2>
                <p className="text-sm text-gray-600 mt-1">Preparing images for bulk ad creation</p>
              </div>
            </div>
            <div className="w-full mt-4">
              <ProgressBar value={imageUploadProgress} />
            </div>
            <div className="flex justify-between items-center mt-3">
              <p className="text-sm font-medium text-gray-700">{imageUploadProgress}% complete</p>
              <p className="text-xs text-gray-500">Please wait while images are being uploaded...</p>
            </div>
          </div>
        )}

        {/* Ad Creation Progress */}
        {isProcessing && !isUploadingImages && (
          <div className="rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-800">Creating Ads...</h2>
                <p className="text-sm text-gray-600 mt-1">Processing your bulk ad creation request</p>
              </div>
            </div>
            <div className="w-full mt-4">
              <ProgressBar value={uploadProgress} />
            </div>
            <div className="mt-4 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Progress</span>
                <span className="text-sm font-semibold text-green-600">{uploadProgress}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Ads Processed</span>
                <span className="text-sm font-medium text-gray-800">
                  {submittedCount} / {csvData.length}
                </span>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-green-200">
              <p className="text-xs text-gray-500 text-center">
                ⚡ Processing in batches of {BATCH_SIZE} for optimal performance
              </p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-4">
          <Button
            type="text"
            onClick={() => {
              setNewFile(null)
              setGeneratedCsvFile(null)
              setGeneratedCsvData([])
              setCsvFile(null)
              setCsvData([])
              setError('')
              setSuccessMessage('')
            }}
            disabled={isProcessing}
          >
            Clear
          </Button>
          <Button type="secondary" onClick={handleStartProcessing} disabled={csvData.length === 0 || isProcessing}>
            {isProcessing ? 'Processing...' : 'Create Ads'}
          </Button>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Modal
        type="small"
        isOpen={showConfirmModal}
        setIsOpen={open => {
          // Prevent closing modal while processing
          if (!open && (isProcessing || isUploadingImages)) {
            return
          }
          setShowConfirmModal(open)
        }}
        title="Confirm Bulk Ad Creation"
      >
        <div className="mt-4 mb-6">
          <p className="text-gray-700">
            Create <strong>{csvData.length} ads</strong>? This will process in batches of {BATCH_SIZE}.
          </p>
        </div>
        <div className="flex justify-end gap-4">
          <Button type="text" onClick={() => setShowConfirmModal(false)} disabled={isProcessing || isUploadingImages}>
            Cancel
          </Button>
          <Button
            type="secondary"
            onClick={() => {
              setShowConfirmModal(false)
              processBulkAds()
            }}
            disabled={isProcessing || isUploadingImages}
          >
            Confirm
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default BulkAdsCreation
